import { useSetAtom } from 'jotai';
import { Skeleton } from 'primereact/skeleton';
import { useCallback, useMemo, useState } from 'react';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
} from 'recharts';
import { selectWidgetAIAtom } from '~/atoms/dashboardAtom';
import { usePopout } from '~/contexts/PopoutContext';
import { formatCurrency } from '~/lib/money';
import { WidgetActions } from '../common/WidgetActions';

type RechartsTooltipProps = TooltipProps<number, string>;

const tooltipFormatter: NonNullable<RechartsTooltipProps['formatter']> = (
  value,
  _name,
  entry,
) => {
  const currency = (entry?.payload as { currency?: string })?.currency;
  if (currency && currency.trim() !== '') {
    return formatCurrency(currency, value as number);
  }
  return (value as number).toLocaleString();
};

export type DynamicPieChartWidgetProps<
  T extends Record<string, unknown> = Record<string, unknown>,
> = {
  data: T[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  // Function to map each data item to { name, value }
  mapSlice: (item: T) => { name: string; value: number; currency?: string };
};

// Finance-inspired colors from CSS variables
const COLORS = [
  'var(--chart-color-profit)', // Green (profit)
  'var(--chart-color-stable)', // Blue (stable)
  'var(--chart-color-caution)', // Amber (caution)
  'var(--chart-color-luxury)', // Purple (luxury)
  'var(--chart-color-tech)', // Cyan (tech)
  'var(--chart-color-energy)', // Orange (energy)
  'var(--chart-color-growth)', // Light Green (growth)
  'var(--chart-color-conservative)', // Indigo (conservative)
  'var(--chart-color-disruptive)', // Pink (disruptive)
  'var(--chart-color-loss)', // Red (loss)
];

const renderCurrencyLabel = (data: { currency?: string; value: number }) => {
  if (data.currency && data.currency.trim() !== '') {
    return formatCurrency(data.currency, data.value);
  }
  return data.value.toLocaleString();
};

export const DynamicPieChartWidget = <
  T extends Record<string, unknown> = Record<string, unknown>,
>({
  data,
  isLoading,
  error,
  refetch,
  handleRemoveWidget,
  widgetId,
  title = 'Dynamic Pie Chart',
  mapSlice,
}: DynamicPieChartWidgetProps<T>) => {
  const [parseError, setParseError] = useState<Error | null>(null);
  const setSelectWidgetAI = useSetAtom(selectWidgetAIAtom);
  const { openPopout } = usePopout();

  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );

  const handlePopout = useCallback(() => {
    openPopout(
      widgetId,
      {
        type: 'DynamicPieChartWidget',
        props: {
          data,
          isLoading,
          error,
          refetch,
          handleRemoveWidget,
          widgetId,
          title,
          mapSlice,
        },
      },
      {
        width: 900,
        height: 600,
        title: title,
      },
    );
  }, [
    data,
    isLoading,
    error,
    refetch,
    handleRemoveWidget,
    widgetId,
    title,
    mapSlice,
    openPopout,
  ]);

  const chartData = useMemo(() => {
    try {
      return data.map(mapSlice);
    } catch (error) {
      setParseError(error as Error);
      return [];
    }
  }, [data, mapSlice]);

  // Determine if any slice contains currency information
  const hasCurrency = useMemo(
    () =>
      chartData.some(
        (d: { currency?: string }) => d.currency && d.currency.trim() !== '',
      ),
    [chartData],
  );

  const noData = useMemo(() => {
    return !chartData.length || chartData.every((entry) => entry.value === 0);
  }, [chartData]);

  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full" />
      </div>
    );
  }

  if (error || parseError) {
    return (
      <div className="h-full hover-container">
        <div className="w-full flex justify-content-between align-items-center">
          <h3 className="mb-0">{title}</h3>
          <div className="align-items-center flex flex-row align-self-start">
            <WidgetActions
              onRemove={removeWidget}
              onPopout={handlePopout}
              widgetId={widgetId}
            />
          </div>
        </div>
        <div className="flex justify-content-center align-items-center h-full">
          <div className="p-error">Something went wrong.</div>
        </div>
      </div>
    );
  }

  if (noData) {
    return (
      <div className="h-full hover-container">
        <div className="w-full flex justify-content-between align-items-center">
          <h3 className="mb-0">{title}</h3>
          <div className="align-items-center flex flex-row align-self-start">
            <WidgetActions
              onRemove={removeWidget}
              onPopout={handlePopout}
              widgetId={widgetId}
            />
          </div>
        </div>
        <div className="flex justify-content-center align-items-center h-full">
          <div>No data found.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="hover-container h-full" id={widgetId}>
      <div className="flex justify-content-between align-items-center">
        <div className="w-full flex justify-content-between align-items-center">
          <div className="flex align-items-center gap-3">
            <h3 className="mb-0">{title}</h3>
          </div>
          <div className="align-items-center flex flex-row align-self-start">
            <WidgetActions
              onRemove={removeWidget}
              onRefresh={() => refetch()}
              onAddToChat={setSelectWidgetAI}
              onPopout={handlePopout}
              widgetId={widgetId}
            />
          </div>
        </div>
      </div>
      <div
        className="mt-3"
        style={{ height: 'calc(100% - 3.4rem)', marginLeft: '0' }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius="80%"
              label={hasCurrency ? renderCurrencyLabel : true}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`slice-${entry.name}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip formatter={tooltipFormatter} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
